<template>
    <div>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
            <v-btn-toggle v-model="brush" mandatory dense>
                <v-btn v-for="t in brushes" :key="t.key" small :value="t.key">
                    <span class="swatch" :style="{ background: t.color }"></span>{{ t.label }}
                </v-btn>
            </v-btn-toggle>
            <v-text-field v-model="search" placeholder="Search staff" prepend-inner-icon="mdi-magnify"
                dense outlined hide-details clearable style="max-width: 200px;"></v-text-field>
            <v-autocomplete v-model="sectionId" :items="sections" item-text="name" item-value="id"
                placeholder="All sections" dense outlined hide-details clearable style="max-width: 240px;"></v-autocomplete>
            <v-checkbox v-model="onlyPlanned" label="Only staff with plans" dense hide-details class="mt-0 pt-0"></v-checkbox>
            <v-spacer></v-spacer>
            <span v-if="changeCount" class="caption">{{ summary }}</span>
            <v-btn small text :disabled="!changeCount" @click="discard">Discard</v-btn>
            <v-btn small color="primary" :disabled="!changeCount" @click="saveDialog = { open: true, reason: '' }">
                <v-icon small left>mdi-content-save-outline</v-icon>Save ({{ changeCount }})
            </v-btn>
        </div>
        <div class="caption grey--text mb-2">
            Pick {{ brushes.map(t => t.label).join(' or ') }}, then click or drag across the days on a staff row (click a marked day again to clear it).
            Leave shows what each person has left in their HRMS leave year and stops at it; WFH is one day a week (a new day replaces the week's other), never in a
            week with a public holiday. Click a planned bar to remove it, a name to open that person's month. Save when done — one reason for all;
            each person is told.
        </div>

        <v-alert v-if="failed.length" type="error" dense text dismissible class="caption" @input="failed = []">
            Not saved — fix these and save again:
            <div v-for="f in failed" :key="f.staff_id + f.type">{{ f.name }} · {{ f.label }}: {{ f.message }}</div>
        </v-alert>

        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
        <team-timeline
            v-if="data"
            :year="year"
            :month="month"
            :staff="staffShown"
            :entries="data.entries"
            :type-by-key="typeByKey"
            :holidays="data.holidays"
            :title-for="entryTitle"
            paint
            :pending="pendingByStaff"
            :removing="removing"
            :badges="badges"
            @paint="onPaint"
            @entry-click="toggleRemove"
            @staff-click="openMonth"
        />

        <v-dialog v-model="saveDialog.open" max-width="440px">
            <v-card>
                <v-card-title class="subtitle-1">Save the plan changes</v-card-title>
                <v-card-text>
                    <div v-for="line in saveLines" :key="line" class="body-2">{{ line }}</div>
                    <v-textarea v-model="saveDialog.reason" label="Reason (required)" outlined dense auto-grow rows="2" counter="1000" class="mt-3"
                        hint="Kept in each person's schedule history and sent to them." persistent-hint></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="saveDialog.open = false">Cancel</v-btn>
                    <v-btn small color="primary" :disabled="!saveDialog.reason.trim()" :loading="saving" @click="save">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="monthView.open" max-width="820px">
            <v-card v-if="monthView.staff">
                <v-card-title class="subtitle-1">
                    {{ monthView.staff.name }}
                    <span class="caption grey--text ml-2">{{ monthView.staff.section }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="monthView.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <month-schedule :year="year" :month="month" :staff-id="monthView.staff.staff_id" history @changed="changed" />
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// HR's planner (HR Staff › Calendar › Planner): every active staff member on
// the timeline with their plans (MonthlyScheduleService::calendar, plans
// only). HR picks a type, paints days on many rows, marks planned entries for
// removal, then saves it all at once with one reason (bulkPlan: each person's
// plan on its own, the usual rules; what failed is listed and stays marked).
// Marks are kept across months until saved or discarded. The backend's
// `balances` (each person's HRMS leave years over the month, with the days
// left in each) and `wfh_blocks` (weeks a person can't take WFH: position,
// service) stop marks that can't be saved.
// Each mark of a leave type is one day: off days are never marked for a type
// that doesn't count them.
import TeamTimeline from './TeamTimeline.vue'
import MonthSchedule from './MonthSchedule.vue'

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default {
    name: 'HrPlanner',
    components: { TeamTimeline, MonthSchedule },
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
    },
    data: () => ({
        data: null,
        loading: false,
        brush: null, // type key being painted
        search: '',
        sectionId: null,
        onlyPlanned: false,
        pending: {}, // "staffId|date" → type key: days to add
        removing: {}, // entry id → { staff_id, entry_id, type }: entries to remove
        failed: [],
        saving: false,
        saveDialog: { open: false, reason: '' },
        monthView: { open: false, staff: null },
    }),
    computed: {
        brushes() {
            return this.data ? this.data.types : []
        },
        brushType() {
            return this.typeByKey[this.brush] || null
        },
        /**
         * Each person's HRMS leave years of the leave brush over this month (the backend's `balances`:
         * from, to, left, projected) less the days marked in them, plus their entries marked for removal.
         */
        periodsLeft() {
            const t = this.brushType
            const base = t && this.data.balances && this.data.balances[t.key]
            if (!base) return null
            const out = {}
            Object.entries(base).forEach(([id, list]) => { out[id] = list.map(p => ({ ...p })) })
            const periodOf = (id, date) => (out[id] || []).find(p => p.from <= date && p.to >= date)
            Object.entries(this.pending).forEach(([key, type]) => {
                const [id, date] = key.split('|')
                const p = type === t.key && periodOf(id, date)
                if (p) p.left -= 1
            })
            Object.values(this.removing).forEach(r => {
                const p = r.type === t.key && periodOf(r.staff_id, r.from_date)
                if (p) p.left += r.days || 0
            })
            return out
        },
        badges() {
            const t = this.brushType
            const out = {}
            if (!t || !this.data) return out
            if (this.periodsLeft) {
                const n = v => Math.round(v * 10) / 10
                this.staffShown.forEach(s => {
                    const list = this.periodsLeft[s.staff_id] || []
                    out[s.staff_id] = !list.length
                        ? { text: 'No HRMS period', warn: true, title: `No ${t.label} period in HRMS — HR needs to set it up before leave can be planned.` }
                        : {
                            text: list.map((p, i) => (i ? `${n(p.left)} from ${this.fmtDate(p.from)}` : `${n(p.left)} left`)).join(' · '),
                            warn: list.some(p => p.left <= 0),
                            title: list.map(p => `${t.label} left in the leave year ${this.fmtFullDate(p.from)} – ${this.fmtFullDate(p.to)}: ${n(p.left)}`
                                + (p.projected ? ' (HRMS creates this year when it starts)' : '')).join('\n')
                                + '\nThe HRMS balance less the leave already planned (and the days marked here).',
                        }
                })
            } else if (t.is_wfh) {
                const open = this.data.weeks.filter(w => !w.blocked).map(w => w.week_start)
                Object.entries(this.data.wfh_blocks || {}).forEach(([id, weeks]) => {
                    const reasons = [...new Set(Object.values(weeks))].join(' ')
                    out[id] = open.every(w => weeks[w])
                        ? { text: 'No WFH', warn: true, title: reasons }
                        : { text: 'WFH limited', title: Object.entries(weeks).map(([w, why]) => `Week of ${this.fmtDate(w)}: ${why}`).join('\n') }
                })
            }
            return out
        },
        typeByKey() {
            const map = {}
            this.brushes.forEach(t => { map[t.key] = t })
            return map
        },
        today() {
            return ymd(new Date())
        },
        holidaySet() {
            const set = {}
            if (this.data) this.data.holidays.forEach(h => { set[h.date] = true })
            return set
        },
        entriesByStaff() {
            const map = {}
            if (this.data) this.data.entries.forEach(e => { (map[e.staff_id] = map[e.staff_id] || []).push(e) })
            return map
        },
        pendingByStaff() {
            const map = {}
            Object.entries(this.pending).forEach(([key, type]) => {
                const [id, date] = key.split('|')
                ;(map[id] = map[id] || []).push({ date, type })
            })
            return map
        },
        sections() {
            if (!this.data) return []
            const byId = {}
            this.data.staff.forEach(s => { byId[s.section_id] = s.section })
            return Object.entries(byId)
                .map(([id, name]) => ({ id: Number(id), name }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        },
        staffShown() {
            if (!this.data) return []
            const q = (this.search || '').trim().toLowerCase()
            return this.data.staff.filter(s =>
                (!this.sectionId || s.section_id === this.sectionId)
                && (!q || `${s.empno} ${s.name}`.toLowerCase().includes(q))
                && (!this.onlyPlanned || this.entriesByStaff[s.staff_id] || this.pendingByStaff[s.staff_id]))
        },
        changeCount() {
            return Object.keys(this.pending).length + Object.keys(this.removing).length
        },
        summary() {
            const add = Object.keys(this.pending).length
            const remove = Object.keys(this.removing).length
            return [add && `${add} day${add === 1 ? '' : 's'} to add`, remove && `${remove} to remove`].filter(Boolean).join(' · ')
        },
        saveLines() {
            const lines = []
            this.brushes.forEach(t => {
                const keys = Object.keys(this.pending).filter(k => this.pending[k] === t.key)
                if (!keys.length) return
                const staff = new Set(keys.map(k => k.split('|')[0])).size
                lines.push(`${t.label}: ${keys.length} day${keys.length === 1 ? '' : 's'} for ${staff} staff`)
            })
            const remove = Object.keys(this.removing).length
            if (remove) lines.push(`Remove ${remove} planned entr${remove === 1 ? 'y' : 'ies'}`)
            return lines
        },
    },
    watch: {
        year() { this.load() },
        month() { this.load() },
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.loading = true
            this.$axios.get('/api/monthly-schedule/calendar', { params: { year: this.year, month: this.month, all: 1, plans: 1 } })
                .then(resp => {
                    this.data = resp.data
                    if (!this.typeByKey[this.brush]) this.brush = (this.brushes.find(t => t.is_leave) || this.brushes[0] || {}).key
                })
                .finally(() => this.loading = false)
        },
        changed() {
            this.load()
            this.$emit('changed')
        },
        /** A drag (or click) over from..to on a staff row: mark those days, or clear them when it starts on a marked day. */
        onPaint(row, from, to) {
            const id = row.staff_id
            if (this.pending[`${id}|${from}`]) {
                this.datesBetween(from, to).forEach(d => this.$delete(this.pending, `${id}|${d}`))
                return
            }
            const type = this.brushType
            if (!type) return
            if (type.is_wfh) return this.paintWfh(id, from)
            let dates = this.datesBetween(from, to)
                .filter(d => d >= this.today && (type.holiday_included || !this.isOff(d)) && !this.plannedOn(id, d))
            if (this.periodsLeft) {
                // Each day comes off the HRMS leave year it falls in; stop where one runs out.
                const list = (this.periodsLeft[id] || []).map(p => ({ ...p }))
                if (!list.length) {
                    this.$toast.info(`${row.name} has no ${type.label} period in HRMS — HR needs to set it up first.`)
                    return
                }
                const kept = []
                let full = null
                dates.forEach(d => {
                    const p = list.find(x => x.from <= d && x.to >= d)
                    if (p && p.left >= 1) {
                        p.left -= 1
                        kept.push(d)
                    } else {
                        full = full || p || { from: d, to: d }
                    }
                })
                if (kept.length < dates.length) {
                    this.$toast.info(`${row.name} has no more ${type.label} left in the leave year ${this.fmtFullDate(full.from)} – ${this.fmtFullDate(full.to)}.`)
                }
                dates = kept
            }
            dates.forEach(d => this.$set(this.pending, `${id}|${d}`, type.key))
        },
        /** WFH: one day per week (MonthlySchedule weeks) — it replaces the week's other mark or WFH day. */
        paintWfh(id, date) {
            const week = this.data.weeks.find(w => w.dates.includes(date))
            if (date < this.today || this.isOff(date) || !week) return
            if (week.blocked) {
                const h = week.blocked === 'public_holiday' && week.block.holidays[0]
                this.$toast.info(h ? `No WFH in the week of ${this.fmtDate(week.week_start)} — ${h.name} (${this.fmtDate(h.date)}).` : "WFH isn't open that week.")
                return
            }
            const why = ((this.data.wfh_blocks || {})[id] || {})[week.week_start]
            if (why) {
                this.$toast.info(why)
                return
            }
            const existing = (this.entriesByStaff[id] || []).find(e => this.typeByKey[e.type]?.is_wfh && week.dates.includes(e.from_date))
            if (existing && (existing.started || existing.from_date < this.today)) {
                this.$toast.info('Their WFH day that week has started or was given up for the office — it stays.')
                return
            }
            if (this.plannedOn(id, date, existing)) return
            week.dates.forEach(d => { if (this.pending[`${id}|${d}`] === this.brush) this.$delete(this.pending, `${id}|${d}`) })
            if (existing) this.$set(this.removing, existing.id, { staff_id: id, entry_id: existing.id, type: existing.type, days: existing.days, from_date: existing.from_date })
            this.$set(this.pending, `${id}|${date}`, this.brush)
        },
        /** A day off: Fri/Sat or a public holiday (HRMS may not have next year's holidays yet). */
        isOff(date) {
            return !!this.holidaySet[date] || [5, 6].includes(new Date(`${date}T00:00:00`).getDay())
        },
        /** A planned entry of the staff member covers the day (apart from ones marked for removal, or `except`). */
        plannedOn(id, date, except = null) {
            return (this.entriesByStaff[id] || []).some(e => e !== except && !this.removing[e.id] && e.from_date <= date && e.to_date >= date)
        },
        toggleRemove(e) {
            if (e.source !== 'schedule') return
            if (this.removing[e.id]) return this.$delete(this.removing, e.id)
            if (e.started || e.from_date < this.today) {
                this.$toast.info('That has already started (or was given up for the office) — it stays as the record.')
                return
            }
            this.$set(this.removing, e.id, { staff_id: e.staff_id, entry_id: e.id, type: e.type, days: e.days, from_date: e.from_date })
        },
        discard() {
            this.pending = {}
            this.removing = {}
            this.failed = []
        },
        save() {
            const add = {}
            Object.entries(this.pending).forEach(([key, type]) => {
                const [id, date] = key.split('|')
                const k = `${id}|${type}`
                ;(add[k] = add[k] || { staff_id: Number(id), type, dates: [] }).dates.push(date)
            })
            this.saving = true
            this.$axios.post('/api/monthly-schedule/bulk', { reason: this.saveDialog.reason.trim(), add: Object.values(add), remove: Object.values(this.removing) })
                .then(resp => this.afterSave(resp.data.data))
                .catch(err => { if (err.response && err.response.data && err.response.data.data) this.afterSave(err.response.data.data) })
                .finally(() => this.saving = false)
        },
        /** Saved plans drop their marks; the failed ones stay marked, listed with why. */
        afterSave(result) {
            const failed = new Set(result.failed.map(f => `${f.staff_id}|${f.type}`))
            const keep = (id, type) => failed.has(`${id}|${type}`)
            const pending = {}
            Object.entries(this.pending).forEach(([key, type]) => { if (keep(key.split('|')[0], type)) pending[key] = type })
            const removing = {}
            Object.values(this.removing).forEach(r => { if (keep(r.staff_id, r.type)) removing[r.entry_id] = r })
            this.pending = pending
            this.removing = removing
            this.failed = result.failed
            this.saveDialog.open = false
            if (result.saved) this.changed()
        },
        openMonth(row) {
            this.monthView = { open: true, staff: row }
        },
        entryTitle(e) {
            const s = this.data.staff.find(x => x.staff_id === e.staff_id) || {}
            const t = this.typeByKey[e.type] || { label: e.type }
            const f = d => { const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y}` }
            return `${s.empno} ${s.name} — ${t.label}\n${e.from_date === e.to_date ? f(e.from_date) : `${f(e.from_date)} – ${f(e.to_date)}`}\n`
                + (e.approved ? 'Approved' : e.submitted ? 'Waiting for approval' : 'Not sent')
                + (e.notes ? `\n${e.notes}` : '')
                + (this.removing[e.id] ? '\n(to be removed — click again to keep)' : '\n(click to remove)')
        },
        fmtDate(s) {
            const [, m, d] = s.split('-')
            return `${d}/${m}`
        },
        fmtFullDate(s) {
            const [y, m, d] = s.split('-')
            return `${d}/${m}/${y}`
        },
        datesBetween(from, to) {
            const out = []
            for (let d = new Date(`${from}T00:00:00`); ymd(d) <= to; d.setDate(d.getDate() + 1)) out.push(ymd(d))
            return out
        },
    },
}
</script>

<style scoped>
.swatch {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    margin-right: 6px;
}
</style>
